import { useState } from "react";
import { toast } from "sonner";
import { Gift, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { api, formatApiError, money } from "@/lib/api";

export const IssueFreeDialog = ({ quote, onDone }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const issue = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/quotes/${quote.id}/issue-free`, { recipient_email: email.trim() || null, recipient_name: name, note });
      setResult(data);
      toast.success(`Policy ${data.policy_number} issued at no charge`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o && result) { setResult(null); onDone(); } }}>
      <DialogTrigger asChild><Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700" data-testid={`admin-issue-free-btn-${quote.id}`}><Gift className="mr-1 h-4 w-4" /> Issue free</Button></DialogTrigger>
      <DialogContent data-testid="admin-issue-free-dialog">
        <DialogHeader>
          <DialogTitle>Issue policy at no charge</DialogTitle>
          <DialogDescription>{quote.business_name || quote.full_name} · current owner {quote.user_email}. Leave the email blank to issue it to the current owner, or enter any person or company to issue it to them.</DialogDescription>
        </DialogHeader>
        {result ? (
          <div className="space-y-2 rounded-md bg-emerald-50 p-4 text-sm" data-testid="admin-issue-free-result">
            <p>Policy <b className="font-mono">{result.policy_number}</b> issued to {result.user_email}.</p>
            {result.temp_password && <p>New account created. Temporary password: <b className="font-mono" data-testid="admin-issue-free-temp-password">{result.temp_password}</b> (share it privately; it is not shown again).</p>}
          </div>
        ) : (
          <div className="grid gap-4">
            <div className="space-y-1.5"><Label htmlFor="if-email">Issue to email (optional)</Label><Input id="if-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-issue-free-email-input" /></div>
            <div className="space-y-1.5"><Label htmlFor="if-name">Recipient name (needed for a new account)</Label><Input id="if-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="admin-issue-free-name-input" /></div>
            <div className="space-y-1.5"><Label htmlFor="if-note">Note (optional)</Label><Input id="if-note" value={note} onChange={(e) => setNote(e.target.value)} data-testid="admin-issue-free-note-input" /></div>
            <Button onClick={issue} disabled={busy} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="admin-issue-free-submit-btn">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Issue at no charge</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const WaiveDialog = ({ policyId, balance, onDone }) => {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  if (!(balance > 0)) return null;
  const waive = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/policies/${policyId}/waive`, { amount: amount ? Number(amount) : null, reason });
      toast.success("Amount waived");
      setOpen(false);
      setAmount("");
      setReason("");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" data-testid="admin-waive-btn">Waive amount due</Button></DialogTrigger>
      <DialogContent data-testid="admin-waive-dialog">
        <DialogHeader><DialogTitle>Waive amount due</DialogTitle><DialogDescription>Current balance {money(balance)}. Recorded as a credit in the ledger.</DialogDescription></DialogHeader>
        <div className="grid gap-4">
          <div className="space-y-1.5"><Label htmlFor="w-amt">Amount (blank = full balance)</Label><Input id="w-amt" type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="admin-waive-amount-input" /></div>
          <div className="space-y-1.5"><Label htmlFor="w-reason">Reason</Label><Input id="w-reason" placeholder="e.g. Billing error on May payment" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="admin-waive-reason-input" /></div>
          <Button onClick={waive} disabled={busy || reason.trim().length < 3} className="bg-blue-700 text-white hover:bg-blue-800" data-testid="admin-waive-submit-btn">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Waive</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
